import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/** The bare calendar date the API binds to a nullable DateOnly. */
export const API_DATE_ONLY_FORMAT = 'YYYY-MM-DD';

/**
 * The forms a date-only value can arrive in from the template builder. The
 * slash form is what DatePickerInput emits (config.DEFAULT_DATE_FORMAT); the
 * dash form is what the API returns and is accepted here so the conversion is
 * idempotent.
 */
const ACCEPTED_FORMATS = ['YYYY/MM/DD', API_DATE_ONLY_FORMAT];

/**
 * Converts a date-only picker value to the bare "YYYY-MM-DD" the API expects
 * for a nullable DateOnly, returning null for empty or unparseable input so a
 * cleared field persists as null rather than failing to bind as "".
 *
 * Parsing is strict against the calendar formats above and never goes through
 * Date or UTC, so the date entered is the date sent regardless of the admin's
 * timezone. A full ISO timestamp is deliberately rejected rather than
 * truncated, because truncating one would reintroduce the day shift.
 *
 * @example
 * toApiDateOnly('2026/09/01') // "2026-09-01"
 * toApiDateOnly('')           // null
 */
export const toApiDateOnly = (value?: string | null): string | null => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const date = dayjs(value.trim(), ACCEPTED_FORMATS, true);

  return date.isValid() ? date.format(API_DATE_ONLY_FORMAT) : null;
};

export default toApiDateOnly;
